import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  MdOutlineDescription,
  MdSearch,
  MdOutlineFilterList,
} from "react-icons/md";
import { questionnaireService } from "services/questionnaireService";
import { Questionnaire } from "services/questionnaireService";

const QuestionnairesList: React.FC = () => {
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  const fetchQuestionnaires = async () => {
    try {
      setLoading(true);
      const response = await questionnaireService.getQuestionnaires();
      if (response.success) {
        setQuestionnaires(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to fetch questionnaires");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestionnaires = questionnaires.filter((questionnaire) => {
    // Filter by search term
    const matchesSearch =
      searchTerm === "" ||
      questionnaire.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      questionnaire.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      questionnaire.description
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    // Filter by active status
    const matchesActive =
      filterActive === null || questionnaire.is_active === filterActive;

    return matchesSearch && matchesActive;
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (value: boolean | null) => {
    setFilterActive(value);
  };

  return (
    <div className="mt-3 grid h-full gap-5">
      <div className="flex flex-col">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Cuestionarios
          </h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar cuestionarios..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              />
              <MdSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-500" />
            </div>
            <div className="relative">
              <button
                onClick={() => {
                  const dropdown = document.getElementById("filter-dropdown");
                  if (dropdown) {
                    dropdown.classList.toggle("hidden");
                  }
                }}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
              >
                <MdOutlineFilterList className="h-5 w-5" />
                <span>Filtrar</span>
              </button>
              <div
                id="filter-dropdown"
                className="absolute right-0 top-full z-10 mt-1 hidden w-48 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-navy-600 dark:bg-navy-700"
              >
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleFilterChange(null)}
                    className={`rounded-md px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-600 ${
                      filterActive === null
                        ? "bg-gray-100 dark:bg-navy-600"
                        : ""
                    }`}
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => handleFilterChange(true)}
                    className={`rounded-md px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-600 ${
                      filterActive === true
                        ? "bg-gray-100 dark:bg-navy-600"
                        : ""
                    }`}
                  >
                    Activos
                  </button>
                  <button
                    onClick={() => handleFilterChange(false)}
                    className={`rounded-md px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-navy-600 ${
                      filterActive === false
                        ? "bg-gray-100 dark:bg-navy-600"
                        : ""
                    }`}
                  >
                    Inactivos
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-brand-500"></div>
          </div>
        ) : error ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-lg font-semibold text-red-500">{error}</div>
          </div>
        ) : filteredQuestionnaires.length === 0 ? (
          <Card extra="w-full p-6">
            <div className="flex h-32 items-center justify-center">
              <p className="text-lg text-gray-500">
                No se encontraron cuestionarios.
              </p>
            </div>
          </Card>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filteredQuestionnaires.map((questionnaire) => (
              <Card
                key={questionnaire.id}
                extra="w-full p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
                onClick={() =>
                  navigate(`/admin/questionnaires/${questionnaire.id}`)
                }
              >
                <div className="flex h-full flex-col">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-white">
                        <MdOutlineDescription className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-navy-700 dark:text-white">
                          {questionnaire.name}
                        </h3>
                        <p className="text-xs text-gray-600">
                          {questionnaire.code}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`h-2 w-2 rounded-full ${
                        questionnaire.is_active ? "bg-green-500" : "bg-red-500"
                      }`}
                      title={questionnaire.is_active ? "Activo" : "Inactivo"}
                    ></div>
                  </div>
                  <p className="mb-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                    {questionnaire.description}
                  </p>
                  <div className="mt-auto border-t border-gray-200 pt-3 dark:border-navy-600">
                    <div className="flex justify-between">
                      <div className="text-xs text-gray-500">
                        <span>Tipo de evaluación: </span>
                        <span className="font-medium">
                          {questionnaire.scoring_type}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        <span>ID: </span>
                        <span className="font-medium">{questionnaire.id}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionnairesList;
